"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import * as React from "react";
import { useWidgetEnv } from "@/lib/use-widget-env";
import { cn } from "@/lib/utils";

/**
 * Radix-based Select primitive — shadcn shape, brand-tokenised. Used
 * by the Profile tab's ENUM / ARRAY field controls and the language
 * picker. Keyboard navigation, type-ahead, and roving focus come from
 * Radix; styling here only swaps shadcn's neutral palette for the
 * widget's `wx-*` tokens so re-skinning the widget recolors the
 * dropdown for free.
 *
 * Mounted via `Portal` so the listbox escapes the widget's clipped /
 * rounded surfaces and never gets cut off by overflow boundaries
 * (the widget panel uses `rounded-wx-md` + `overflow-hidden` on
 * several wrappers).
 *
 * **Shadow DOM** — the React-component embed mounts the widget inside
 * a Shadow DOM. Radix's `Portal` defaults to `document.body` which is
 * OUTSIDE the shadow root, so the dropdown rendered there missed the
 * widget's tokenised stylesheet (`bg-wx-bg` resolved to nothing,
 * leaving the listbox transparent). `SelectContent` reads
 * `env.themeRoot` and pins the portal inside the shadow root when
 * available; iframe / web-component builds fall through to the
 * default body portal where styling reaches it normally.
 */
/**
 * `modal={false}` by default —
 *
 *   1. Radix's default `modal={true}` writes `overflow: hidden` to
 *      `document.body` while the dropdown is open. In a regular app
 *      that's fine; inside the widget's Shadow DOM that lock LEAKS to
 *      the HOST page, freezing the page's own scroll the second the
 *      visitor opens any picker. We never want that.
 *   2. `modal={true}` also installs a focus trap that interacts oddly
 *      with the Shadow DOM root — in some browsers the trap re-routes
 *      events away from the Radix popover before Radix's own listeners
 *      fire, so the dropdown silently fails to open.
 *
 * Both regress to the well-behaved non-modal flavour: the dropdown
 * still closes on outside-click + Escape (Radix handles that with or
 * without `modal`), keyboard nav still works inside the listbox, and
 * the host page stays scrollable.
 *
 * Override with `<Select modal>` if a specific call site needs the
 * modal behaviour (none does today).
 */
// Radix's runtime accepts `modal` on `Select.Root` (documented in the
// Radix UI API), but our pinned `.d.ts` for `@radix-ui/react-select`
// declares it on a private sub-type and doesn't surface it via
// `ComponentProps<typeof Root>`. Extend the prop type locally to expose
// it without bumping the dependency.
type SelectRootProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> & {
  modal?: boolean;
};
export function Select({ modal = false, ...props }: SelectRootProps) {
  const Root = SelectPrimitive.Root as React.ComponentType<SelectRootProps>;
  return <Root modal={modal} {...props} />;
}
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 w-full items-center justify-between gap-2 rounded-wx-sm border border-wx-border bg-wx-bg px-3 py-2 text-sm text-wx-fg",
      "placeholder:text-wx-fg-subtle",
      "focus:border-wx-primary focus:outline-none focus:ring-2 focus:ring-wx-primary/20",
      "disabled:cursor-not-allowed disabled:bg-wx-bg-elevated/60 disabled:text-wx-fg-muted",
      "[&>span]:line-clamp-1",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown size={14} className="opacity-60" aria-hidden="true" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  // Pin the portal inside the widget's shadow root when running in a
  // shadow-DOM embed (React component path) so Tailwind classes on
  // the dropdown actually resolve. Iframe / web-component / Storybook
  // hosts leave `themeRoot` undefined; `undefined` falls through to
  // Radix's default of `document.body`.
  const env = useWidgetEnv();
  const container = env.themeRoot ?? undefined;
  return (
    <SelectPrimitive.Portal container={container}>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        // The widget panel sits at `z-2147483601` (above any host
        // chrome). The dropdown must sit above the panel itself, so we
        // use a value one above the launcher/panel range. Without this
        // the dropdown opens but gets painted underneath the panel and
        // the visitor can't see / interact with it.
        className={cn(
          "relative z-2147483602 max-h-72 min-w-32 overflow-hidden rounded-wx-sm border border-wx-border bg-wx-bg text-wx-fg shadow-lg",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pr-2 pl-7 text-sm outline-none",
      "focus:bg-wx-bg-elevated focus:text-wx-fg",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "data-[state=checked]:font-medium",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check size={12} className="text-wx-primary" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;
